/**
 * useTaskBoard — Phase 2
 *
 * Backed by React Query when VITE_TASK_BOARD_USE_API=true.
 * Falls back to MOCK_TASKS (full Phase-1 behaviour) when the flag is false.
 *
 * External interface is identical to Phase 1 — TaskBoard.tsx needs no changes.
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Task, TaskStatus, TaskPriority, TaskUser, SortConfig, TEAMS } from '../types';
import type { TaskTeam } from '../types';
import { MOCK_TASKS, MOCK_USERS } from '../mocks/mockData';
import { TaskApi, CreateTaskPayload, MoveTaskPayload, UpdateTaskPayload } from '../api/taskApi';

const USE_API = import.meta.env.VITE_TASK_BOARD_USE_API === 'true';

let taskCounter = MOCK_TASKS.length + 1;

const PRIORITY_ORDER: Record<TaskPriority, number> = {
  urgent: 0, high: 1, medium: 2, low: 3,
};

// ── Shared helpers ────────────────────────────────────────────────────────────

function buildNewTask(
  data: {
    title: string; description: string; assigneeId: number;
    priority: TaskPriority; status?: TaskStatus; team: TaskTeam; dueDate: string | null; labels: string[];
  },
  currentUser: TaskUser
): Task {
  const assignee = MOCK_USERS.find(u => u.id === data.assigneeId) ?? currentUser;
  const now = new Date().toISOString();
  return {
    id: `task-${Date.now()}`,
    key: `TB-${taskCounter++}`,
    title: data.title,
    description: data.description,
    assignee,
    reporter: currentUser,
    priority: data.priority,
    status: data.status ?? 'todo',
    team: data.team,
    dueDate: data.dueDate,
    labels: data.labels,
    createdAt: now,
    updatedAt: now,
    statusHistory: [],
    comments: [],
  };
}

function sortTasks(tasks: Task[], config: SortConfig): Task[] {
  return [...tasks].sort((a, b) => {
    let cmp = 0;
    if (config.field === 'priority') {
      cmp = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
    } else if (config.field === 'title') {
      cmp = a.title.localeCompare(b.title);
    } else if (config.field === 'dueDate') {
      const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      cmp = ad - bd;
    } else {
      cmp = new Date(a[config.field]).getTime() - new Date(b[config.field]).getTime();
    }
    return config.direction === 'asc' ? cmp : -cmp;
  });
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTaskBoard(currentUser: TaskUser) {
  const queryClient = useQueryClient();
  const QUERY_KEY = useMemo(() => ['tasks', currentUser.id] as const, [currentUser.id]);

  // UI-only state — stays local in both modes
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<Record<TaskStatus, SortConfig>>({
    backlog:     { field: 'createdAt', direction: 'desc' },
    todo:        { field: 'createdAt', direction: 'desc' },
    in_progress: { field: 'priority',  direction: 'asc'  },
    done:        { field: 'updatedAt', direction: 'desc' },
  });

  // ── Data source ─────────────────────────────────────────────────────────────

  const TEAM_FALLBACK: string[] = TEAMS;
  const { data: teams = TEAM_FALLBACK } = useQuery<string[]>({
    queryKey: ['task-teams'],
    queryFn: TaskApi.getTeams,
    enabled: USE_API,
    initialData: USE_API ? undefined : TEAM_FALLBACK,
    staleTime: Infinity,
    placeholderData: TEAM_FALLBACK,
    retry: 1,
  });

  const { data: tasks = MOCK_TASKS, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => TaskApi.getTasks(currentUser.id),
    enabled: USE_API,
    // Seed with mock data — shown immediately while fetching, and kept as fallback if API fails
    placeholderData: MOCK_TASKS,
    initialData: USE_API ? undefined : MOCK_TASKS,
    staleTime: USE_API ? 30_000 : Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // ── Cache helpers (used by all mutations) ───────────────────────────────────

  const setCacheTasks = useCallback(
    (updater: (prev: Task[]) => Task[]) => {
      queryClient.setQueryData<Task[]>(QUERY_KEY, prev => updater(prev ?? MOCK_TASKS));
    },
    [queryClient, QUERY_KEY]
  );

  const updateCacheTask = useCallback(
    (taskId: string, updater: (t: Task) => Task) => {
      setCacheTasks(prev => prev.map(t => t.id === taskId ? updater(t) : t));
    },
    [setCacheTasks]
  );

  // ── addTask ─────────────────────────────────────────────────────────────────

  const { mutate: _createTaskMutation } = useMutation({
    mutationFn: (payload: CreateTaskPayload) => TaskApi.createTask(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (_, __, context: { tempId?: string } | undefined) => {
      // Rollback optimistic task on failure
      if (context?.tempId) {
        setCacheTasks(prev => prev.filter(t => t.id !== context.tempId));
      }
    },
  });

  const addTask = useCallback(
    (data: {
      title: string; description: string; assigneeId: number;
      priority: TaskPriority; status?: TaskStatus; team: TaskTeam; dueDate: string | null; labels: string[];
    }) => {
      const newTask = buildNewTask(data, currentUser);

      // Optimistic insert (works in both modes)
      setCacheTasks(prev => [newTask, ...prev]);

      if (USE_API) {
        const tempId = newTask.id;
        _createTaskMutation(
          {
            title: data.title,
            description: data.description,
            assignee_id: data.assigneeId,
            priority: data.priority,
            ...(data.status ? { status: data.status } : {}),
            team: data.team,
            due_date: data.dueDate,
            labels: data.labels,
          },
          {
            onError: () => {
              // Rollback the optimistic task if the API call fails
              setCacheTasks(prev => prev.filter(t => t.id !== tempId));
            },
          }
        );
      }
    },
    [currentUser, setCacheTasks, _createTaskMutation]
  );

  // ── moveTask ────────────────────────────────────────────────────────────────

  const { mutate: _moveTaskMutation } = useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: MoveTaskPayload }) =>
      TaskApi.moveTask(taskId, payload),
    onError: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const moveTask = useCallback(
    (taskId: string, newStatus: TaskStatus, deprioritizeReason?: string) => {
      const now = new Date().toISOString();

      // Optimistic update
      updateCacheTask(taskId, task => ({
        ...task,
        status: newStatus,
        updatedAt: now,
        statusHistory: [
          ...task.statusHistory,
          {
            from: task.status,
            to: newStatus,
            changedBy: currentUser,
            changedAt: now,
            ...(deprioritizeReason ? { reason: deprioritizeReason } : {}),
          },
        ],
      }));

      if (USE_API) {
        _moveTaskMutation({
          taskId,
          payload: { status: newStatus, ...(deprioritizeReason ? { reason: deprioritizeReason } : {}) },
        });
      }
    },
    [currentUser, updateCacheTask, _moveTaskMutation]
  );

  // ── updateTask ──────────────────────────────────────────────────────────────

  const { mutate: _updateTaskMutation } = useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: UpdateTaskPayload }) =>
      TaskApi.updateTask(taskId, payload),
    onSuccess: (updatedTask) => {
      updateCacheTask(updatedTask.id, () => updatedTask);
    },
    onError: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const updateTask = useCallback(
    (taskId: string, updates: Partial<Task>) => {
      const now = new Date().toISOString();

      // Optimistic update
      updateCacheTask(taskId, task => ({ ...task, ...updates, updatedAt: now }));

      if (USE_API) {
        const payload: UpdateTaskPayload = {
          ...(updates.title       !== undefined && { title: updates.title }),
          ...(updates.description !== undefined && { description: updates.description }),
          ...(updates.assignee    !== undefined && { assignee_id: updates.assignee.id }),
          ...(updates.priority    !== undefined && { priority: updates.priority }),
          ...(updates.team        !== undefined && { team: updates.team }),
          ...(updates.dueDate     !== undefined && { due_date: updates.dueDate }),
          ...(updates.labels      !== undefined && { labels: updates.labels }),
          ...(updates.status      !== undefined && { status: updates.status }),
        };
        _updateTaskMutation({ taskId, payload });
      }
    },
    [updateCacheTask, _updateTaskMutation]
  );

  // ── addComment ──────────────────────────────────────────────────────────────

  const { mutate: _addCommentMutation } = useMutation({
    mutationFn: ({ taskId, text }: { taskId: string; text: string }) =>
      TaskApi.addComment(taskId, { text }),
    onSuccess: (updatedTask, { taskId }) => {
      // Replace task with server version so comment has correct id/timestamp
      if (USE_API) {
        updateCacheTask(taskId, () => updatedTask);
      }
    },
    onError: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const addComment = useCallback(
    (taskId: string, text: string) => {
      const newComment = {
        id: `comment-${Date.now()}`,
        author: currentUser,
        text,
        createdAt: new Date().toISOString(),
      };

      // Optimistic insert
      updateCacheTask(taskId, task => ({
        ...task,
        comments: [...task.comments, newComment],
      }));

      if (USE_API) {
        _addCommentMutation({ taskId, text });
      }
    },
    [currentUser, updateCacheTask, _addCommentMutation]
  );

  // ── Derived values ──────────────────────────────────────────────────────────

  const getTasksByStatus = useCallback(
    (status: TaskStatus): Task[] =>
      sortTasks(
        tasks.filter(t => t.status === status),
        sortConfig[status]
      ),
    [tasks, sortConfig]
  );

  const setSortForColumn = useCallback(
    (status: TaskStatus, config: SortConfig) =>
      setSortConfig(prev => ({ ...prev, [status]: config })),
    []
  );

  const totalCounts = useMemo(
    () => ({
      backlog:     tasks.filter(t => t.status === 'backlog').length,
      todo:        tasks.filter(t => t.status === 'todo').length,
      in_progress: tasks.filter(t => t.status === 'in_progress').length,
      done:        tasks.filter(t => t.status === 'done').length,
    }),
    [tasks]
  );

  return {
    tasks,
    teams,
    isLoading: USE_API && isLoading && !isError,
    apiError: USE_API && isError,
    draggedTaskId,
    setDraggedTaskId,
    sortConfig,
    setSortForColumn,
    getTasksByStatus,
    moveTask,
    addTask,
    updateTask,
    addComment,
    totalCounts,
    allUsers: MOCK_USERS,
  };
}
